import axios from 'axios'

/** The parts of an axios error this module reads. */
interface AxiosLikeError {
  code?: string
  message?: string
  request?: unknown
  response?: { status?: number }
}

/**
 * Apply a timeout to every axios request so a hung API call fails with a timeout
 * error instead of spinning forever. Set at module load so it covers all calls,
 * regardless of whether the interceptors have been wired up yet.
 *
 * Two minutes rather than the 60s sa-screens uses (src/api/Interceptors.js): the
 * claims API takes around 30 seconds for a single page of 25 records, and the
 * service order list asks it once per selected dealer group.
 */
export const REQUEST_TIMEOUT_MS = 120000

axios.defaults.timeout = REQUEST_TIMEOUT_MS

/** Axios reports a timeout as ECONNABORTED/ETIMEDOUT rather than an HTTP status. */
export const isTimeoutError = (error: unknown): boolean =>
  (error as AxiosLikeError)?.code === 'ECONNABORTED' ||
  (error as AxiosLikeError)?.code === 'ETIMEDOUT'

/**
 * A message worth showing a user. The point is that a failed request never looks
 * like an empty result - "no records" and "we could not ask" are different things.
 */
export const describeRequestError = (error: unknown): string => {
  if (isTimeoutError(error)) {
    return 'The request took too long and timed out. Please narrow your filters and try again.'
  }

  const failure = error as AxiosLikeError
  const status = failure?.response?.status
  if (status === 401 || status === 403) {
    // The response interceptor in opw.api.ts already handles the session toast.
    return 'You are not authorised to view this. Please sign in again.'
  }
  if (status === 504 || status === 502) {
    return 'The service did not respond in time. Please try again.'
  }
  if (status) {
    return `The request failed (${status}). Please try again.`
  }
  if (failure?.request) {
    return 'Could not reach the service. Check your connection and try again.'
  }

  return failure?.message || 'Something went wrong. Please try again.'
}
